import { useEffect, useState } from "react";
import axios from "axios";
import { Monitor, Apple, Loader2, Save, CheckCircle2, AlertTriangle } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { API_URL } from "@/lib/links";

const planRows = [
  { id: "trial", label: "7-day free trial" },
  { id: "monthly", label: "Monthly" },
  { id: "yearly", label: "Yearly" },
];

const PlanToggles = ({ plans, onChange }) => (
  <div className="mt-6 rounded-xl border border-line bg-panel2/60 p-4" data-testid="downloads-plans">
    <p className="text-sm font-medium text-body">Downloads per subscription</p>
    <p className="mt-1 text-xs text-muted">Turn a subscription off to grey out its download buttons on the site.</p>
    <div className="mt-4 grid gap-3 sm:grid-cols-3">
      {planRows.map((p) => (
        <div key={p.id} className="flex items-center justify-between gap-3 rounded-lg border border-line/60 bg-ink/60 px-3 py-2.5" data-testid={`downloads-plan-row-${p.id}`}>
          <span className="text-sm text-silver">{p.label}</span>
          <div className="flex items-center gap-2">
            <span className={`text-[10px] font-semibold uppercase tracking-[0.15em] ${plans[p.id] ? "text-emerald-300" : "text-muted"}`} data-testid={`downloads-plan-status-${p.id}`}>{plans[p.id] ? "On" : "Off"}</span>
            <Switch checked={plans[p.id]} onCheckedChange={(v) => onChange({ ...plans, [p.id]: v })} className="data-[state=checked]:bg-blue data-[state=unchecked]:bg-line" data-testid={`downloads-plan-toggle-${p.id}`} />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const platforms = [
  { id: "windows", label: "Windows download", icon: Monitor },
  { id: "mac", label: "Mac / iOS download", icon: Apple },
];

const PlatformRow = ({ p, value, onChange }) => {
  const live = value.enabled && value.url;
  return (
    <div className="rounded-xl border border-line bg-panel2/60 p-4" data-testid={`downloads-row-${p.id}`}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2.5 text-sm font-medium text-body"><p.icon size={16} className="text-blue-text" /> {p.label}</div>
        <div className="flex items-center gap-3">
          <span className={`text-xs font-semibold uppercase tracking-[0.15em] ${live ? "text-emerald-300" : "text-muted"}`} data-testid={`downloads-status-${p.id}`}>{live ? "Live" : "Off"}</span>
          <Switch checked={value.enabled} onCheckedChange={(enabled) => onChange({ ...value, enabled })} className="data-[state=checked]:bg-blue data-[state=unchecked]:bg-line" data-testid={`downloads-toggle-${p.id}`} />
        </div>
      </div>
      <input className="field mt-3 text-sm" placeholder="https://… installer link" value={value.url} onChange={(e) => onChange({ ...value, url: e.target.value })} data-testid={`downloads-url-${p.id}`} />
      {value.enabled && !value.url && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-200" data-testid={`downloads-missing-url-${p.id}`}><AlertTriangle size={12} /> Paste the installer link to make this button live.</p>
      )}
    </div>
  );
};

export const DownloadsPanel = ({ headers }) => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/downloads`).then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API_URL}/admin/downloads`, settings, { headers });
      setSettings(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return null;
  return (
    <div className="card mt-8 p-6 sm:p-7" data-testid="admin-downloads-panel">
      <h2 className="text-lg font-semibold tracking-tight text-body">Download buttons</h2>
      <p className="mt-1 text-sm text-muted">Turn each platform's download on or off across the whole site. Changes go live the moment you save.</p>
      <div className="mt-5 grid gap-4 md:grid-cols-2">
        {platforms.map((p) => <PlatformRow key={p.id} p={p} value={settings[p.id]} onChange={(v) => setSettings({ ...settings, [p.id]: v })} />)}
      </div>
      <PlanToggles plans={settings.plans || { trial: true, monthly: true, yearly: true }} onChange={(plans) => setSettings({ ...settings, plans })} />
      <button type="button" onClick={save} disabled={saving} className="btn btn-primary mt-5 !px-5 !py-2.5 text-sm" data-testid="downloads-save-button">
        {saving ? <Loader2 size={14} className="animate-spin" /> : saved ? <CheckCircle2 size={14} /> : <Save size={14} />} {saved ? "Saved" : "Save changes"}
      </button>
    </div>
  );
};
